import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import {
    getIstQuarter,
    getIIndQuarter,
    getIIIrdQuarter,
    getIVthQuarter,
    getVthQuarter,
    getVIthQuarter,
    clssInfo,
    IndexIndividualForm,
    FormEdit,
    DestroyForm
} from '../../Api'

import {
    Container,
    ContainerDivs,
    Header,
    HeaderInfo,
    Title,
    Subtitle,
    HeaderBadge,
    Toolbar,
    SearchInput,
    Badge,
    InputArea,
    Label,
    Select,
    Section,
    SectionHeader,
    SectionTitle,
    SectionHint,
    List,
    EmpStdt,
    EmpChecked,
    Span,
    ItemTag,
    EmptyState,
    ClosedNotice,
    SkeletonCard,
    SkeletonLine,
    ToGoBack,
    SignMessageButtonText,
    SignMessageButtonTextBold,
    Overlay,
    Modal,
    ModalTitle,
    ModalText,
    ModalButton,
    FormCard,
    FormHeader,
    FormTitle,
    FormSubtitle,
    FormError,
    FormActions,
    SaveButton,
    CancelButton,
    DeleteButton,
    QuillWrap,
    ViewBody
} from './style'

// ── helpers fora do componente ─────────────────────────────────────
const safeParse = (raw) => {
    try { return raw ? JSON.parse(raw) : null } catch { return null }
}

const extractId = (v) => {
    if (v && typeof v === 'object') return String(v._id ?? v.id ?? '')
    return v != null ? String(v) : ''
}

// mapeia o rótulo do período para o campo de quarter no model do relatório
const BIMESTRE_FIELD = {
    '1º BIMESTRE': 'id_iStQuarter',
    '2º BIMESTRE': 'id_iiNdQuarter',
    '3º BIMESTRE': 'id_iiiRdQuarter',
    '4º BIMESTRE': 'id_ivThQuarter',
    '5º BIMESTRE': 'id_vThQuarter',
    '6º BIMESTRE': 'id_viThQuarter'
}

// toolbar do editor (fora do componente para não recriar a cada render)
const QUILL_MODULES = {
    toolbar: [
        [{ font: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['bold', 'italic', 'underline'],
        [{ color: [] }, { background: [] }],
        ['clean']
    ]
}

const IndividualForm = () => {
    const navigate = useNavigate()
    const mountedRef = useRef(true)

    // ── contexto da turma/professor ──────────────────────────────────
    const [Selectbimonthly, setSelectbimonthly] = useState(null)
    const [bimonthly, setBimonthly] = useState([])      // quarters disponíveis (fallback select)
    const [year, setYear] = useState('')
    const [turma, setTurma] = useState(null)
    const [id_class, setId_class] = useState('')
    const [myId, setMyId] = useState('')
    const [regentId, setRegentId] = useState('')
    const [regent02Id, setRegent02Id] = useState('')
    const [physicalId, setPhysicalId] = useState('')
    const [assessmentRegime, setAssessmentRegime] = useState('')

    // ── listas ───────────────────────────────────────────────────────
    const [stdt, setStdt] = useState([])         // alunos sem relatório
    const [checked, setChecked] = useState([])   // alunos com relatório

    // ── ui ───────────────────────────────────────────────────────────
    const [loading, setLoading] = useState(true)        // carga inicial (turma + quarters)
    const [loadingList, setLoadingList] = useState(false) // carga da lista de alunos
    const [showAlert, setShowAlert] = useState(false)
    const [busca, setBusca] = useState('')
    const [debouncedBusca, setDebouncedBusca] = useState('')

    // ── modal de relatório (ver / editar / apagar) ──────────────────
    const [viewingRow, setViewingRow] = useState(null)   // linha de "checked" selecionada
    const [modalMode, setModalMode] = useState('view')   // 'view' | 'edit' | 'delete'
    const [editedDescription, setEditedDescription] = useState('')
    const [savingEdit, setSavingEdit] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [modalError, setModalError] = useState('')

    const periodLabel = assessmentRegime === 'TRIMESTRAL' ? 'Trimestre' : 'Bimestre'

    // status de abertura do período para o tipo de professor
    const open = useMemo(() => {
        if (!Selectbimonthly?.bimonthly || !turma?.dailyStatus) return 'aberto'
        const st = turma.dailyStatus[Selectbimonthly.bimonthly]
        if (!st) return 'aberto'
        const amIPhysical = !!physicalId && myId === physicalId
        return amIPhysical ? st.physicalEducationTeacher : st.regentTeacher
    }, [Selectbimonthly, turma, myId, physicalId])

    const isOpen = open === 'aberto'

    useEffect(() => {
        mountedRef.current = true
        return () => { mountedRef.current = false }
    }, [])

    // ── debounce da busca ────────────────────────────────────────────
    useEffect(() => {
        const t = setTimeout(() => setDebouncedBusca(busca), 250)
        return () => clearTimeout(t)
    }, [busca])

    // ── scroll lock + ESC quando o modal está aberto ────────────────
    useEffect(() => {
        if (!viewingRow) return
        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const onKey = (e) => {
            if (e.key === 'Escape' && !savingEdit && !deleting) closeView()
        }
        window.addEventListener('keydown', onKey)
        return () => {
            document.body.style.overflow = prevOverflow
            window.removeEventListener('keydown', onKey)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewingRow, savingEdit, deleting])

    // ── FASE 1: turma + quarters (paralelizado) ─────────────────────
    useEffect(() => {
        (async () => {
            setLoading(true)

            const idSchool = safeParse(sessionStorage.getItem('id-school'))
            const idClass = sessionStorage.getItem('class-info')
            const selectbi = safeParse(sessionStorage.getItem('Selectbimonthly'))
            const id_employee = safeParse(localStorage.getItem('Id_employee'))

            try {
                const resClass = await clssInfo(idClass)
                if (!mountedRef.current) return

                const turmaData = resClass?.data?.data?.[0] || null
                const yearValue = resClass?.data?.data?.find(c => c.year)?.year || ''

                // quarters em PARALELO (antes eram 6 awaits sequenciais)
                const [q1, q2, q3, q4, q5, q6] = await Promise.all([
                    getIstQuarter(yearValue, idSchool),
                    getIIndQuarter(yearValue, idSchool),
                    getIIIrdQuarter(yearValue, idSchool),
                    getIVthQuarter(yearValue, idSchool),
                    getVthQuarter(yearValue, idSchool),
                    getVIthQuarter(yearValue, idSchool)
                ])
                if (!mountedRef.current) return

                const pick = (q) => q?.data?.data?.find(r => r) || null
                const quarters = [pick(q1), pick(q2), pick(q3), pick(q4), pick(q5), pick(q6)]
                    .filter(Boolean)

                setTurma(turmaData)
                setYear(yearValue)
                setId_class(idClass)
                setSelectbimonthly(selectbi)
                setBimonthly(quarters)
                setMyId(extractId(id_employee))
                setRegentId(extractId(safeParse(sessionStorage.getItem('classRegentTeacher'))))
                setRegent02Id(extractId(safeParse(sessionStorage.getItem('classRegentTeacher02'))))
                setPhysicalId(extractId(safeParse(sessionStorage.getItem('physicalEducationTeacher'))))
                setAssessmentRegime(
                    sessionStorage.getItem('assessmentRegime') || pick(q1)?.assessmentRegime || ''
                )
            } catch (err) {
                console.error('Erro ao carregar turma/períodos:', err)
            } finally {
                if (mountedRef.current) setLoading(false)
            }
        })()
    }, [])

    // ── FASE 2: lista de alunos do período selecionado ──────────────
    useEffect(() => {
        if (!Selectbimonthly?.bimonthly || !turma) return

        const quarterIdKey = BIMESTRE_FIELD[Selectbimonthly.bimonthly]
        if (!quarterIdKey) return

        let cancelled = false
        ;(async () => {
            setLoadingList(true)
            try {
                // se este professor for o regente 02, busca pelos relatórios do regente
                const amIRegent02 = !!regent02Id && regent02Id === myId
                const teacherForQuery = amIRegent02 ? regentId : myId

                const res = await IndexIndividualForm({
                    year,
                    id_class,
                    id_teacher: teacherForQuery,
                    [quarterIdKey]: Selectbimonthly._id
                })
                if (cancelled || !mountedRef.current) return

                const existing = (res?.data || []).filter(Boolean)
                const realizedIds = new Set(
                    existing.map(r => r?.id_student?._id).filter(Boolean)
                )

                const allStudents = turma?.id_student || []
                const pending = allStudents.filter(s => s && !realizedIds.has(s._id))

                setStdt(pending)
                setChecked(existing)
            } catch (err) {
                console.error('Erro ao buscar relatórios:', err)
            } finally {
                if (!cancelled && mountedRef.current) setLoadingList(false)
            }
        })()

        return () => { cancelled = true }
    }, [Selectbimonthly, turma, year, id_class, myId, regentId, regent02Id])

    // ── busca aplicada às duas listas ───────────────────────────────
    const filteredStdt = useMemo(() => {
        const q = debouncedBusca.trim().toLowerCase()
        const base = [...stdt]
            .filter(s => s && s._id)
            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
        if (!q) return base
        return base.filter(s => s.name.toLowerCase().includes(q))
    }, [stdt, debouncedBusca])

    const filteredChecked = useMemo(() => {
        const q = debouncedBusca.trim().toLowerCase()
        const base = [...checked]
            .filter(s => s && s.id_student && s.id_student._id)
            .sort((a, b) => a.id_student.name.localeCompare(b.id_student.name, 'pt-BR'))
        if (!q) return base
        return base.filter(s => s.id_student.name.toLowerCase().includes(q))
    }, [checked, debouncedBusca])

    // ── handlers ─────────────────────────────────────────────────────
    const handleBimonthlyChange = useCallback((e) => {
        const val = e.target.value ? JSON.parse(e.target.value) : null
        setSelectbimonthly(val)
        if (val) sessionStorage.setItem('Selectbimonthly', JSON.stringify(val))
    }, [])

    // criar relatório novo para um aluno sem ficha
    const handleIndividualForm = useCallback((student) => {
        if (!regentId) {
            setShowAlert(true)
            return
        }
        sessionStorage.setItem('stdt', JSON.stringify(student))
        sessionStorage.setItem('nmstdt', JSON.stringify(student.name))
        navigate('/form')
    }, [regentId, navigate])

    // abrir relatório existente — agora em modal na própria página
    const openView = useCallback((row) => {
        setViewingRow(row)
        setModalMode('view')
        setModalError('')
    }, [])

    const closeView = useCallback(() => {
        if (savingEdit || deleting) return
        setViewingRow(null)
        setModalMode('view')
        setModalError('')
        setEditedDescription('')
    }, [savingEdit, deleting])

    const startEditing = useCallback(() => {
        if (!viewingRow) return
        if (!isOpen) {
            setModalError('O período está fechado. Contate o Supervisor.')
            return
        }
        setEditedDescription(viewingRow.description || '')
        setModalError('')
        setModalMode('edit')
    }, [viewingRow, isOpen])

    // salva a edição via FormEdit; atualiza a lista "checked" em memória
    const handleSaveEdit = useCallback(async () => {
        if (savingEdit || !viewingRow) return
        if (!isOpen) {
            setModalError('O período está fechado. Contate o Supervisor.')
            return
        }
        setModalError('')
        setSavingEdit(true)
        try {
            const res = await FormEdit({
                update_idForm: viewingRow._id,
                editedDescription
            })
            if (res) {
                setChecked(prev => prev.map(r =>
                    r._id === viewingRow._id
                        ? { ...r, description: editedDescription }
                        : r
                ))
                setViewingRow(prev => prev
                    ? { ...prev, description: editedDescription }
                    : prev)
                setModalMode('view')
            } else {
                setModalError('Não foi possível salvar. Tente novamente.')
            }
        } catch (err) {
            console.error('Erro ao salvar relatório:', err)
            setModalError('Erro ao salvar o relatório. Tente novamente.')
        } finally {
            setSavingEdit(false)
        }
    }, [savingEdit, viewingRow, editedDescription, isOpen])

    const startDelete = useCallback(() => {
        if (!viewingRow) return
        if (!isOpen) {
            setModalError('O período está fechado. Contate o Supervisor.')
            return
        }
        setModalError('')
        setModalMode('delete')
    }, [viewingRow, isOpen])

    // confirma a exclusão via DestroyForm; remove de "checked" e devolve
    // o aluno para a lista de pendentes (sem reload).
    const handleDestroy = useCallback(async () => {
        if (deleting || !viewingRow) return
        if (!isOpen) {
            setModalError('O período está fechado. Contate o Supervisor.')
            return
        }
        const deletedId = viewingRow._id
        const student = viewingRow.id_student
        setModalError('')
        setDeleting(true)
        try {
            await DestroyForm(deletedId)
            setChecked(prev => prev.filter(r => r._id !== deletedId))
            // devolve o aluno para "pendentes" (anti-eco: evita duplicar)
            if (student?._id) {
                setStdt(prev => prev.some(s => s._id === student._id)
                    ? prev
                    : [...prev, student])
            }
            setViewingRow(null)
            setModalMode('view')
        } catch (err) {
            console.error('Erro ao apagar relatório:', err)
            setModalError('Erro ao apagar o relatório. Tente novamente.')
        } finally {
            setDeleting(false)
        }
    }, [deleting, viewingRow, isOpen])

    const goBack = useCallback(() => navigate(-1), [navigate])

    // ── render: skeleton de carregamento ────────────────────────────
    if (loading) {
        return (
            <Container>
                <ContainerDivs>
                    <Header>
                        <HeaderInfo><Title>Relatório Individual</Title></HeaderInfo>
                    </Header>
                    {[1, 2, 3].map(i => (
                        <SkeletonCard key={i}>
                            <SkeletonLine $w="40%" $h="18px" />
                            <SkeletonLine $w="80%" />
                            <SkeletonLine $w="60%" />
                        </SkeletonCard>
                    ))}
                </ContainerDivs>
            </Container>
        )
    }

    const periodoLabel = Selectbimonthly?.bimonthly
        ? Selectbimonthly.bimonthly.replace('BIMESTRE', periodLabel.toUpperCase())
        : ''

    return (
        <Container>
            <ContainerDivs>
                <Header>
                    <HeaderInfo>
                        <Title>Relatório Individual</Title>
                        {periodoLabel && <Subtitle>{periodoLabel}</Subtitle>}
                    </HeaderInfo>
                    {Selectbimonthly && isOpen && (
                        <HeaderBadge>
                            {filteredChecked.length} de {filteredChecked.length + filteredStdt.length} concluídos
                        </HeaderBadge>
                    )}
                </Header>

                {/* SELEÇÃO DE PERÍODO — só aparece se não veio da central */}
                {!Selectbimonthly && (
                    <InputArea>
                        <Label>Selecione o {periodLabel}</Label>
                        <Select
                            id="id-bimonthly"
                            value=""
                            onChange={handleBimonthlyChange}
                        >
                            <option value="">Selecione</option>
                            {bimonthly
                                .filter(res => assessmentRegime === 'TRIMESTRAL'
                                    ? !res.bimonthly.includes('4º') : true)
                                .map(res => {
                                    const label = assessmentRegime === 'TRIMESTRAL'
                                        ? res.bimonthly.replace('BIMESTRE', 'TRIMESTRE')
                                        : res.bimonthly
                                    return (
                                        <option
                                            key={res._id}
                                            value={JSON.stringify({
                                                _id: res._id,
                                                bimonthly: res.bimonthly,
                                                statusSupervisor: res.statusSupervisor
                                            })}
                                        >
                                            {label}
                                        </option>
                                    )
                                })}
                        </Select>
                    </InputArea>
                )}

                {/* CONTEÚDO DO PERÍODO */}
                {Selectbimonthly && (
                    isOpen ? (
                        <>
                            <Toolbar>
                                <SearchInput
                                    type="text"
                                    placeholder="Buscar aluno..."
                                    value={busca}
                                    onChange={(e) => setBusca(e.target.value)}
                                    aria-label="Buscar aluno"
                                />
                                <Badge>
                                    {filteredStdt.length + filteredChecked.length} alunos
                                </Badge>
                            </Toolbar>

                            {loadingList ? (
                                [1, 2].map(i => (
                                    <SkeletonCard key={i}>
                                        <SkeletonLine $w="30%" $h="16px" />
                                        <SkeletonLine $w="100%" />
                                        <SkeletonLine $w="100%" />
                                    </SkeletonCard>
                                ))
                            ) : (
                                <>
                                    {/* Pendentes */}
                                    <Section>
                                        <SectionHeader>
                                            <SectionTitle>
                                                Pendentes <Badge>{filteredStdt.length}</Badge>
                                            </SectionTitle>
                                        </SectionHeader>
                                        <SectionHint>Toque em um aluno para adicionar o relatório.</SectionHint>
                                        {filteredStdt.length === 0 ? (
                                            <List>
                                                <EmptyState>
                                                    {debouncedBusca
                                                        ? 'Nenhum aluno pendente para esta busca.'
                                                        : 'Todos os alunos já têm relatório neste período. 🎉'}
                                                </EmptyState>
                                            </List>
                                        ) : (
                                            <List>
                                                {filteredStdt.map(s => (
                                                    <EmpStdt key={s._id} onClick={() => handleIndividualForm(s)}>
                                                        <Span>{s.name}</Span>
                                                        <ItemTag>Adicionar</ItemTag>
                                                    </EmpStdt>
                                                ))}
                                            </List>
                                        )}
                                    </Section>

                                    {/* Concluídos */}
                                    <Section>
                                        <SectionHeader $tone="done">
                                            <SectionTitle $tone="done">
                                                Com relatório <Badge>{filteredChecked.length}</Badge>
                                            </SectionTitle>
                                        </SectionHeader>
                                        <SectionHint>Toque para visualizar, editar ou apagar.</SectionHint>
                                        {filteredChecked.length === 0 ? (
                                            <List>
                                                <EmptyState>
                                                    {debouncedBusca
                                                        ? 'Nenhum relatório para esta busca.'
                                                        : 'Nenhum relatório criado ainda neste período.'}
                                                </EmptyState>
                                            </List>
                                        ) : (
                                            <List>
                                                {filteredChecked.map(row => (
                                                    <EmpChecked
                                                        key={row.id_student._id}
                                                        onClick={() => openView(row)}
                                                    >
                                                        <Span>{row.id_student.name}</Span>
                                                        <ItemTag $tone="done">Ver</ItemTag>
                                                    </EmpChecked>
                                                ))}
                                            </List>
                                        )}
                                    </Section>
                                </>
                            )}
                        </>
                    ) : (
                        <ClosedNotice>
                            <span>🔒 Período fechado</span>
                            <span style={{ fontWeight: 400, color: '#5b6b72' }}>
                                Para editar, contate o Diretor ou Supervisor.
                            </span>
                        </ClosedNotice>
                    )
                )}

                <ToGoBack onClick={goBack} role="button" tabIndex={0}>
                    <SignMessageButtonText>Voltar para a</SignMessageButtonText>
                    <SignMessageButtonTextBold>Turma</SignMessageButtonTextBold>
                </ToGoBack>
            </ContainerDivs>

            {/* MODAL: ver / editar / apagar relatório existente */}
            {viewingRow && (() => {
                const nome = viewingRow.id_student?.name || 'Aluno'
                const periodo = Selectbimonthly?.bimonthly
                    ? Selectbimonthly.bimonthly.replace('BIMESTRE', periodLabel.toUpperCase())
                    : ''

                return (
                    <Overlay onClick={closeView}>
                        <FormCard onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">

                            {/* ── VISUALIZAÇÃO ─────────────────────────── */}
                            {modalMode === 'view' && (
                                <>
                                    <FormHeader>
                                        <FormTitle>{nome}</FormTitle>
                                        {periodo && <FormSubtitle>{periodo}</FormSubtitle>}
                                    </FormHeader>

                                    <ViewBody
                                        dangerouslySetInnerHTML={{
                                            __html: viewingRow.description || '<p>Sem descrição.</p>'
                                        }}
                                    />

                                    {modalError && <FormError>{modalError}</FormError>}

                                    <FormActions>
                                        <CancelButton onClick={closeView}>Fechar</CancelButton>
                                        <DeleteButton
                                            onClick={startDelete}
                                            disabled={!isOpen}
                                            title={isOpen ? 'Apagar relatório' : 'Período fechado'}
                                        >
                                            Apagar
                                        </DeleteButton>
                                        <SaveButton
                                            onClick={startEditing}
                                            disabled={!isOpen}
                                            title={isOpen ? 'Editar relatório' : 'Período fechado'}
                                        >
                                            {isOpen ? 'Editar' : 'Período Fechado'}
                                        </SaveButton>
                                    </FormActions>
                                </>
                            )}

                            {/* ── EDIÇÃO ───────────────────────────────── */}
                            {modalMode === 'edit' && (
                                <>
                                    <FormHeader>
                                        <FormTitle>Editando Relatório</FormTitle>
                                        <FormSubtitle>{nome}{periodo ? ` · ${periodo}` : ''}</FormSubtitle>
                                    </FormHeader>

                                    <QuillWrap>
                                        <ReactQuill
                                            theme="snow"
                                            modules={QUILL_MODULES}
                                            value={editedDescription}
                                            onChange={(content) => setEditedDescription(content)}
                                            placeholder="Relatório Individual do Aluno"
                                        />
                                    </QuillWrap>

                                    {modalError && <FormError>{modalError}</FormError>}

                                    <FormActions>
                                        <CancelButton
                                            onClick={() => { setModalMode('view'); setModalError('') }}
                                            disabled={savingEdit}
                                        >
                                            Cancelar
                                        </CancelButton>
                                        <SaveButton onClick={handleSaveEdit} disabled={savingEdit}>
                                            {savingEdit ? 'Salvando...' : 'Salvar'}
                                        </SaveButton>
                                    </FormActions>
                                </>
                            )}

                            {/* ── EXCLUSÃO ─────────────────────────────── */}
                            {modalMode === 'delete' && (
                                <>
                                    <FormHeader>
                                        <FormTitle>Apagar Relatório</FormTitle>
                                        <FormSubtitle>{nome}{periodo ? ` · ${periodo}` : ''}</FormSubtitle>
                                    </FormHeader>

                                    <ModalText>
                                        Tem certeza que deseja apagar o relatório do aluno(a) <strong>{nome}</strong>
                                        {periodo ? ` no ${periodo}` : ''}? Esta ação não pode ser desfeita.
                                    </ModalText>

                                    {modalError && <FormError>{modalError}</FormError>}

                                    <FormActions>
                                        <CancelButton
                                            onClick={() => { setModalMode('view'); setModalError('') }}
                                            disabled={deleting}
                                        >
                                            Cancelar
                                        </CancelButton>
                                        <DeleteButton onClick={handleDestroy} disabled={deleting}>
                                            {deleting ? 'Apagando...' : 'Apagar'}
                                        </DeleteButton>
                                    </FormActions>
                                </>
                            )}

                        </FormCard>
                    </Overlay>
                )
            })()}

            {/* MODAL: turma sem professor regente titular */}
            {showAlert && (
                <Overlay onClick={() => setShowAlert(false)}>
                    <Modal onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                        <ModalTitle>⚠️ Atenção</ModalTitle>
                        <ModalText>
                            Esta turma está <strong>sem um Professor Regente Titular</strong>.
                            Para adicionar o relatório do aluno, é necessário que a turma tenha
                            um <strong>Professor Regente Titular</strong> vinculado.
                        </ModalText>
                        <ModalButton onClick={() => setShowAlert(false)}>Entendi</ModalButton>
                    </Modal>
                </Overlay>
            )}
        </Container>
    )
}

export default IndividualForm